import { useEffect, useRef, useState } from "react";
import { Bell, Check, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const KIND_LABEL = {
  practice_ready: "Practice ready",
  reflection_reminder: "Reflection",
  goal_reminder: "Season goal",
  trial_ending: "Trial",
  subscription: "Subscription",
  product: "Product",
  admin: "From CoachIQ",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const loadCount = () => api.get("/notifications/unread-count").then(r => setCount(r.data.count || 0)).catch(() => {});
  const load = () => { setLoading(true); return api.get("/notifications").then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false)); };

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 45000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); setItems(is => is.map(n => n.id === id ? { ...n, read: true } : n)); loadCount(); };
  const markAll = async () => { await api.post("/notifications/read-all"); setItems(is => is.map(n => ({ ...n, read: true }))); setCount(0); toast.success("All caught up"); };

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)} data-testid="notif-bell" aria-label="Notifications" className="relative p-2 rounded-full hover:bg-gray-100 transition-colors">
        <Bell className="w-[18px] h-[18px] text-gray-600" />
        {count > 0 && (
          <span data-testid="notif-count" className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">{count > 9 ? "9+" : count}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-[340px] max-h-[70vh] overflow-hidden bg-white rounded-2xl shadow-xl border border-gray-100 z-50 flex flex-col" data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="font-display font-bold">Notifications</div>
            <button data-testid="notif-mark-all" onClick={markAll} className="text-xs text-[#007AFF] font-semibold hover:underline inline-flex items-center gap-1"><CheckCheck className="w-3 h-3" /> Mark all read</button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-gray-400 text-sm">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-2xl bg-gray-50 mx-auto flex items-center justify-center mb-3 text-2xl">🔔</div>
                <div className="text-sm font-medium text-gray-800">You're all caught up</div>
                <div className="text-xs text-gray-500 mt-1">Practice reminders and updates will show up here.</div>
              </div>
            ) : items.map(n => (
              <button
                key={n.id}
                onClick={() => { markRead(n.id); if (n.link) { setOpen(false); nav(n.link); } }}
                data-testid={`notif-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${n.read ? "opacity-60" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#007AFF] shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">{KIND_LABEL[n.kind] || n.kind}</div>
                    <div className="text-sm font-semibold text-gray-900 mt-0.5">{n.title}</div>
                    {n.body && <div className="text-xs text-gray-600 mt-1 line-clamp-2">{n.body}</div>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
